/**
 * Sanctum Oversight Panel - Weathered control interface for Sanctum exploration
 * Displays sanctum records with rusty, grounded aesthetic
 */

import React, { useState, useEffect } from 'react';
import { getSoundManager, SFX_KEYS } from '../game/systems/SoundManager';

// Rusty/weathered color palette
const COLORS = {
  darkBg: '#1a1410',
  metalBg: '#2a2420',
  border: '#8B6F47',
  borderLight: '#A0826D',
  accent: '#D4A574',
  accentLight: '#E8C4A0',
  text: '#D4A574',
  textDark: '#8B6F47',
  warning: '#C85A54',
};

interface SanctumTerminalEntry {
  category: 'purpose' | 'intent' | 'records' | 'integrity' | 'breach';
  title: string;
  content: string;
}

interface SanctumTerminalData {
  sanctumNumber: number;
  sanctumName: string;
  sanctumType: 'survival' | 'experiment' | 'preservation';
  entries: SanctumTerminalEntry[];
}

interface SanctumTerminalProps {
  isOpen: boolean;
  onClose: () => void;
  terminalData: SanctumTerminalData | null;
}

// Sanctum-specific category labels based on type
const getCategoryLabels = (sanctumType?: string): Record<string, string> => {
  const baseLabels: Record<string, Record<string, string>> = {
    survival: {
      purpose: ' SANCTUM PURPOSE',
      intent: ' ARCHITECT\'S INTENT',
      records: ' INHABITANT RECORDS',
      integrity: ' SYSTEM INTEGRITY',
      breach: ' BREACH REPORTS',
    },
    experiment: {
      purpose: ' SANCTUM PURPOSE',
      intent: ' ARCHITECT\'S INTENT',
      records: ' SUBJECT PROFILES',
      integrity: ' CONTAINMENT STATUS',
      breach: ' ESCAPE PROTOCOLS',
    },
    preservation: {
      purpose: ' SANCTUM PURPOSE',
      intent: ' ARCHITECT\'S INTENT',
      records: ' HISTORICAL RECORDS',
      integrity: ' ARCHIVE INTEGRITY',
      breach: ' ACCESS VIOLATIONS',
    },
  };
  return baseLabels[sanctumType || 'survival'] || baseLabels.survival;
};


// Rift glitch effect component
const RiftGlitch: React.FC = () => {
  const [glitchActive, setGlitchActive] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() > 0.85) {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 150);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  if (!glitchActive) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        background: `linear-gradient(45deg, transparent 30%, rgba(139, 0, 255, 0.1) 50%, transparent 70%)`,
        animation: 'glitch-scan 0.15s infinite',
        zIndex: 10,
      }}
    />
  );
};

export const ArchitectConduit: React.FC<SanctumTerminalProps> = ({ isOpen, onClose, terminalData }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Play navigation sound when category changes
  useEffect(() => {
    if (selectedCategory) {
      const soundManager = getSoundManager();
      soundManager?.play(SFX_KEYS.RESEARCH_START);
    }
  }, [selectedCategory]);


  if (!isOpen || !terminalData) return null;

  const selectedEntry = terminalData.entries.find(e => e.category === selectedCategory);
  const categories = Array.from(new Set(terminalData.entries.map(e => e.category)));
  const categoryLabels = getCategoryLabels(terminalData.sanctumType);
  
  // Debug: Log if labels are missing
  if (!categoryLabels || Object.keys(categoryLabels).length === 0) {
    console.warn('⚠️ Category labels not found for sanctum type:', terminalData.sanctumType);
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        fontFamily: '"Sometype Mono", monospace',
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes rust-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.9; }
        }
        @keyframes metal-flicker {
          0%, 100% { color: #D4A574; text-shadow: 0 0 8px rgba(212, 165, 116, 0.3); }
          50% { color: #E8C4A0; text-shadow: 0 0 12px rgba(232, 196, 160, 0.4); }
        }
        @keyframes subtle-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        .oversight-container {
          animation: rust-pulse 4s ease-in-out infinite;
        }
      `}</style>

      <div
        className="oversight-container"
        style={{
          width: '90%',
          maxWidth: '1100px',
          height: '85%',
          maxHeight: '750px',
          backgroundColor: COLORS.darkBg,
          border: `2px solid ${COLORS.border}`,
          boxShadow: `
            0 0 30px rgba(139, 111, 71, 0.3),
            inset 0 0 20px rgba(42, 36, 32, 0.8),
            0 4px 8px rgba(0, 0, 0, 0.6)
          `,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <RiftGlitch />
        {/* Header - Oversight Control Panel */}
        <div
          style={{
            background: `linear-gradient(180deg, ${COLORS.metalBg} 0%, ${COLORS.darkBg} 100%)`,
            borderBottom: `2px solid ${COLORS.border}`,
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative', zIndex: 2 }}>
            
            <div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  letterSpacing: '2px',
                  color: COLORS.accent,
                  textShadow: `0 0 8px rgba(212, 165, 116, 0.3)`,
                  animation: 'metal-flicker 4s ease-in-out infinite',
                }}
              >
                SANCTUM OVERSIGHT
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: COLORS.textDark,
                  marginTop: '4px',
                  letterSpacing: '1px',
                  textShadow: `0 0 4px rgba(139, 111, 71, 0.4)`,
                }}
              >
                SANCTUM
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              color: COLORS.accent,
              border: `1px solid ${COLORS.border}`,
              padding: '10px 20px',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              letterSpacing: '1px',
              transition: 'all 0.3s',
              boxShadow: `0 0 8px rgba(139, 111, 71, 0.3)`,
              textShadow: `0 0 4px rgba(139, 111, 71, 0.3)`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = `0 0 16px rgba(212, 165, 116, 0.5)`;
              e.currentTarget.style.backgroundColor = `rgba(212, 165, 116, 0.08)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = `0 0 8px rgba(139, 111, 71, 0.3)`;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            [CLOSE]
          </button>
        </div>

        {/* Main Content Area */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
          {/* Sidebar - Records Navigation */}
          <div
            style={{
              width: '300px',
              background: `linear-gradient(90deg, ${COLORS.darkBg} 0%, ${COLORS.metalBg} 100%)`,
              borderRight: `2px solid ${COLORS.border}`,
              padding: '24px 16px',
              overflowY: 'auto',
              position: 'relative',
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  color: COLORS.accent,
                  fontSize: '11px',
                  fontWeight: 'bold',
                  letterSpacing: '2px',
                  marginBottom: '12px',
                  borderBottom: `1px solid rgba(212, 165, 116, 0.2)`,
                  paddingBottom: '8px',
                  textShadow: `0 0 6px rgba(212, 165, 116, 0.3)`,
                }}
              >
                -- RECORDS --
              </div>
            </div>

            {categories.map((category) => {
              const isSelected = selectedCategory === category;
              const label = categoryLabels[category as keyof typeof categoryLabels] || `${category.toUpperCase()}`;
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  style={{
                    width: '100%',
                    backgroundColor: isSelected ? `rgba(212, 165, 116, 0.12)` : 'transparent',
                    color: isSelected ? COLORS.accent : COLORS.textDark,
                    border: `1px solid ${isSelected ? COLORS.accent : `rgba(139, 111, 71, 0.2)`}`,
                    padding: '7px 8px',
                    marginBottom: '10px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    letterSpacing: '0.5px',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    boxShadow: isSelected ? `0 0 12px rgba(212, 165, 116, 0.25)` : 'none',
                    textShadow: isSelected ? `0 0 6px rgba(212, 165, 116, 0.4)` : 'none',
                  }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = `rgba(212, 165, 116, 0.08)`;
                      e.currentTarget.style.borderColor = `rgba(212, 165, 116, 0.4)`;
                      e.currentTarget.style.color = COLORS.accent;
                    }
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = `rgba(139, 111, 71, 0.2)`;
                      e.currentTarget.style.color = COLORS.textDark;
                    }
                  }}
                >
                  <span>{label}</span>
                </button>
              );
            })}

            <div
              style={{
                marginTop: '32px',
                padding: '7px',
                background: `rgba(200, 90, 84, 0.08)`,
                border: `1px solid rgba(200, 90, 84, 0.3)`,
                fontSize: '10px',
                color: COLORS.warning,
                lineHeight: '1.5',
                letterSpacing: '0.5px',
                textShadow: `0 0 4px rgba(200, 90, 84, 0.3)`,
              }}
            >
              <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>! STRUCTURAL DAMAGE</div>
              Integrity compromised. Estimated stability: {Math.floor(Math.random() * 30 + 70)}%.
            </div>
          </div>

          {/* Content Area - Records Display */}
          <div
            style={{
              flex: 1,
              padding: '16px 20px',
              overflowY: 'auto',
              backgroundColor: COLORS.darkBg,
              position: 'relative',
              background: `radial-gradient(ellipse at center, ${COLORS.metalBg} 0%, ${COLORS.darkBg} 100%)`,
            }}
          >
            {!selectedEntry ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#0A9BA8',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: COLORS.accent,
                    marginBottom: '24px',
                    animation: 'subtle-float 6s ease-in-out infinite',
                    filter: `drop-shadow(0 0 12px rgba(212, 165, 116, 0.3))`,
                    letterSpacing: '2px',
                  }}
                >
                  [READY]
                </div>
                <div
                  style={{
                    fontSize: '20px',
                    color: COLORS.accent,
                    marginBottom: '12px',
                    fontWeight: 'bold',
                    letterSpacing: '1px',
                    textShadow: `0 0 8px rgba(212, 165, 116, 0.3)`,
                  }}
                >
                  SYSTEM READY
                </div>
                <div style={{ fontSize: '14px', maxWidth: '450px', lineHeight: '1.7', color: COLORS.textDark }}>
                  Select a record to access sanctum documentation and status reports.
                </div>
                <div
                  style={{
                    marginTop: '40px',
                    padding: '16px 24px',
                    background: `rgba(212, 165, 116, 0.08)`,
                    border: `1px solid rgba(212, 165, 116, 0.2)`,
                    color: COLORS.accent,
                    fontSize: '12px',
                    letterSpacing: '1px',
                    textShadow: `0 0 6px rgba(212, 165, 116, 0.3)`,
                  }}
                >
                  SANCTUM SYSTEM ONLINE
                </div>
              </div>
            ) : (
              <div>
                {/* Content Header */}
                <div
                  style={{
                    borderBottom: `2px solid rgba(212, 165, 116, 0.2)`,
                    paddingBottom: '16px',
                    marginBottom: '28px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '22px',
                      color: COLORS.accent,
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                      marginBottom: '10px',
                      textShadow: `0 0 8px rgba(212, 165, 116, 0.3)`,
                      animation: 'metal-flicker 4s ease-in-out infinite',
                    }}
                  >
                    {selectedEntry.title.toUpperCase()}
                  </div>
                  <div
                    style={{
                      fontSize: '11px',
                      color: COLORS.textDark,
                      letterSpacing: '1px',
                      textShadow: `0 0 4px rgba(139, 111, 71, 0.3)`,
                    }}
                  >
                    CLASSIFICATION: LEVEL {selectedEntry.category === 'breach' ? '5' : '3'}
                  </div>
                </div>

                {/* Content Body */}
                <div
                  style={{
                    color: COLORS.textDark,
                    fontSize: '14px',
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap',
                    textShadow: `0 0 4px rgba(139, 111, 71, 0.2)`,
                  }}
                >
                  {selectedEntry.content}
                </div>

                {/* Footer Timestamp */}
                <div
                  style={{
                    marginTop: '40px',
                    paddingTop: '20px',
                    borderTop: `1px solid rgba(212, 165, 116, 0.15)`,
                    fontSize: '11px',
                    color: COLORS.textDark,
                    letterSpacing: '0.5px',
                  }}
                >
                  ACCESSED: {new Date().toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer - Sanctum Doctrine */}
        <div
          style={{
            background: `linear-gradient(90deg, ${COLORS.darkBg} 0%, ${COLORS.metalBg} 100%)`,
            color: COLORS.accent,
            padding: '14px 20px',
            borderTop: `2px solid ${COLORS.border}`,
            fontSize: '11px',
            textAlign: 'center',
            letterSpacing: '1px',
            textShadow: `0 0 6px rgba(212, 165, 116, 0.3)`,
          }}
        >
          "In the depths, what was forgotten may yet be preserved" — SANCTUM DOCTRINE
        </div>
      </div>
    </div>
  );
};
